import {
  faBoxes,
  faCartCircleArrowDown,
} from '@fortawesome/pro-regular-svg-icons'
import {
  Box,
  Text,
  Button,
  Flex,
  Image,
  Card,
  type CardProps,
} from '@mantine/core'

import { useMutationUpdateBooking } from '@flecto/core/saas'
import { useTranslation } from '@flecto/translations'
import { type CoreBookingProduct } from '@flecto/types'
import { Badge, BadgeItemCategory, Icon } from '@flecto/ui'

import { useItemPhotoUrl } from '../../../items'
import { useBookingProductAvailabilityCalculator } from '../../hooks/useBookingProductAvailabilityCalculator'
import styles from './styles.module.css'

export type CardBookingProductPreviewProps = Omit<CardProps, 'children'> & {
  bookingProduct?: CoreBookingProduct
  withInsuranceInfo?: boolean
}

export function CardBookingProductPreview({
  bookingProduct,
  ...props
}: CardBookingProductPreviewProps) {
  const { t } = useTranslation('saas', { keyPrefix: 'booking_items' })
  const { product } = bookingProduct ?? {}

  const photo = useItemPhotoUrl({ item: product })

  const {
    mutateAsync: updateBooking,
    isLoading: isResetingBookingItemQuantity,
  } = useMutationUpdateBooking()

  const { isOverbooked, isUnavailable } =
    useBookingProductAvailabilityCalculator({
      bookingId: bookingProduct?.booking.internal_id,
      productId: bookingProduct?.product.id,
      quantityStart: bookingProduct?.quantity,
    })

  const handleResetBookingItemQuantity = () => {
    updateBooking({
      bookingId: bookingProduct?.booking.internal_id,
      booking: {
        booking_items_attributes: [
          {
            id: bookingProduct?.id,
          },
        ],
      },
      options: { force_suggested_booking_cost: true },
    })
  }

  return (
    <Card w={320} {...props}>
      <Card.Section>
        <Image src={photo} alt={product?.title} />
      </Card.Section>
      <BadgeItemCategory
        className={styles.badgeCategory}
        categoryId={product?.category_id}
        withInsuranceInfo
      />
      <Flex direction='column' gap='sm' align='flex-start' mt='md'>
        {isOverbooked && (
          <Box>
            <Badge color='orange' ml={-2}>
              {t('label_overbooking')}
            </Badge>
          </Box>
        )}
        <Text fw={500}>{product?.title}</Text>
      </Flex>
      <Button
        fullWidth
        variant='default'
        mt='md'
        size='sm'
        disabled={!(isUnavailable || isOverbooked)}
        leftSection={<Icon icon={faCartCircleArrowDown} />}
        onClick={handleResetBookingItemQuantity}
        loading={isResetingBookingItemQuantity}
      >
        {t('label_action_reset_quantity')}
      </Button>
      <Button
        component='a'
        fullWidth
        variant='default'
        mt='md'
        size='sm'
        leftSection={<Icon icon={faBoxes} />}
        target='_blank'
        href='/items'
      >
        {t('label_action_top_up_quantity')}
      </Button>
    </Card>
  )
}
